import { useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import { FLEET } from 'utils/SentinelTeam/constants';
import { RightAlignedFlex } from '../components/styles';
import { useManagedDepotsQuery } from './hooks/useManagedDepotsQuery';

type ManagedDepotsType =
  ManagedDepotsQuery['managedAssetGroupings']['collection'][0];
type ColumnProperties = Pick<
  ManagedDepotsType,
  | 'id'
  | 'name'
  | 'totalAssetsEnabled'
  | 'city'
  | 'state'
  | 'status'
  | 'sentinelTeam'
>;

const Depots = () => {
  const [page, setPage] = useState(1);

  const { isSuperUser } = useCurrentUserContext();

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data } = useManagedDepotsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'name',
      title: t`Name`,
      DataCell: ({ rowData: { name } }) => <span>{name}</span>,
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`ID`,
      DataCell: ({ rowData: { id } }) => <span>{id}</span>,
      // sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'fleet',
      title: t`Fleet`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>
          {sentinelTeam?.teamType === FLEET ? sentinelTeam?.name : '-'}
        </span>
      ),
    },
    {
      name: 'team',
      title: t`Team`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{sentinelTeam?.name ?? '-'}</span>
      ),
    },
    {
      name: 'oem_segment',
      title: t`OEM`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{sentinelTeam?.oemSegment?.name || '-'}</span>
      ),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totalAssetsEnabled } }) => (
        <span>{totalAssetsEnabled}</span>
      ),
      sortBy: (direction) => onSort('ENABLED_ASSET_COUNT', direction),
    },
    {
      name: 'city',
      title: t`City`,
      DataCell: ({ rowData: { city } }) => <span>{city || '-'}</span>,
      sortBy: (direction) => onSort('CITY', direction),
    },
    {
      name: 'state',
      title: t`State`,
      DataCell: ({ rowData: { state } }) => <span>{state || '-'}</span>,
      sortBy: (direction) => onSort('STATE', direction),
    },
  ];

  const tableData = data?.managedAssetGroupings.collection ?? [];
  const pagination = data?.managedAssetGroupings.pagination;

  return (
    <>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <Button text={t`Create Depot`} icon={Plus} size="small" />
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Depot by Name</Trans>
          </P>
          <SearchInput name="searchByDepot" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <Table
        data={tableData}
        columns={columns}
        onSort={onSort}
        defaultSort={defaultSort}
        footer={() => (
          <PaginationFooter
            title={t`Depots`}
            setCurrentPage={setPage}
            totalOnPage={tableData.length}
            page={pagination?.page}
            perPage={pagination?.perPage}
            totalEntries={pagination?.totalEntries}
            totalPages={pagination?.totalPages}
          />
        )}
      />
    </>
  );
};

export default Depots;
